import {Divider, Stack, Typography} from '@mui/material';
import {FeaturesEnum, HealthGuideServicesTypeEnum} from 'presentation/constants';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useReverseNetworkOrder} from 'presentation/hooks/useReverseNetworkOrder';
import {NetworkTypes} from 'presentation/modules/guide/health/ui/pages/HealthGuide/types';
import {useAccountInformation, useQueryParams} from 'presentation/pages/private';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {GroupItem} from '../GroupItem';
import {TelehealthCard} from '../TelehealthCard';
import type {NetworkGroupsProps} from './types';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {telemedicine: telemedicineLabel} = translations['pt-br'].commons;
const {
	titleTelemedicineNetwork,
	titleOwnNetwork,
	titleAccreditedNetwork,
	titleNotreDameHapvidaNetwork,
} = translations['pt-br'].privateFlow.guideFlow;

export function NetworkGroups({list}: NetworkGroupsProps) {
	const {SEARCH_PARAMS} = useQueryParams();
	const {serviceType} = SEARCH_PARAMS;
	const {isEarlyAccess} = useAccountInformation();
	const {isEnabled: showTradeNameFlag} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NMFANTASIA_GUIA_MEDICO,
	);
	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();

	const {isContractSuspended} = useContractStatus();

	const {congenereEligibility} = useCongenereEligibilityStore();

	const titles = {
		accreditedNetwork: titleAccreditedNetwork,
		ownNetwork: isNotredameSaude ? titleNotreDameHapvidaNetwork : titleOwnNetwork,
		telemedicineNetwork: titleTelemedicineNetwork,
	};

	const accreditedProps = {
		isGndiSaoPaulo,
		list: list.accredited,
		showTradeNameFlag,
		title: titles.accreditedNetwork,
	};

	const {own = [], telemedicine = [], accredited = []} = list;
	const showTelehealth =
		telemedicine.length > 0 &&
		!isContractSuspended &&
		serviceType === HealthGuideServicesTypeEnum.OUTPATIENT &&
		!isEarlyAccess;

	const {shouldReverseNetworkOrder} = useReverseNetworkOrder({flowType: 'GUIDE'});

	return (
		<Fragment>
			{showTelehealth && !congenereEligibility ? (
				<Stack mb={2}>
					<Typography
						variant="subtitle1"
						color="text.secondary"
						fontWeight={600}
						margin="0 0 8px">
						{telemedicineLabel}
					</Typography>
					<TelehealthCard />
				</Stack>
			) : null}

			<Stack flexDirection={shouldReverseNetworkOrder ? 'column-reverse' : 'column'}>
				<GroupItem
					isGndiSaoPaulo
					list={own}
					showTradeNameFlag={showTradeNameFlag}
					title={titles.ownNetwork}
					type={
						isNotredameSaude
							? NetworkTypes.NOTRE_DAME_HAPVIDA
							: NetworkTypes.SELF_NETWORK
					}
				/>

				{own.length > 0 && accredited.length > 0 ? <Divider sx={{mt: 3, mb: 2}} /> : null}

				<GroupItem
					{...accreditedProps}
					type={
						isNotredameSaude
							? NetworkTypes.ACCREDITED_NOTRE_DAME
							: NetworkTypes.ACCREDITED_NETWORK
					}
				/>
			</Stack>
		</Fragment>
	);
}
